/**
 * API client pour les fonctionnalités communautaires.
 */

import { getToken } from '../utils/auth.js';

const BASE = '/api/community';

/**
 * Récupère les templates autour d'une position.
 * @param {{ lat: number, lng: number, radiusKm?: number, deviceId?: string }} params
 * @returns {Promise<Array>}
 */
export async function fetchNearbyTemplates({ lat, lng, radiusKm = 30, deviceId = '' }) {
  const params = new URLSearchParams({
    lat: String(lat),
    lng: String(lng),
    radius_km: String(radiusKm),
    device_id: deviceId,
  });
  const res = await fetch(`${BASE}/templates?${params}`);
  if (!res.ok) throw new Error('Erreur lors du chargement');
  return res.json();
}

/**
 * Publie un template communautaire.
 * @param {object} data
 * @returns {Promise<object>}
 */
export async function publishTemplate(data) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}/templates`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Erreur lors de la publication');
  }
  return res.json();
}

/**
 * Vote ou retire son vote pour un template.
 * @param {number} templateId
 * @param {string} deviceId
 * @returns {Promise<{ voted: boolean }>}
 */
export async function voteTemplate(templateId, deviceId) {
  const res = await fetch(`${BASE}/templates/${templateId}/vote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ device_id: deviceId }),
  });
  if (!res.ok) throw new Error('Erreur lors du vote');
  return res.json();
}

/**
 * Recherche des magasins communautaires.
 * @param {{ q?: string, lat?: number, lng?: number }} params
 * @returns {Promise<Array>}
 */
export async function searchCommunityStores({ q = '', lat = 0, lng = 0 }) {
  const params = new URLSearchParams({ q, lat: String(lat), lng: String(lng) });
  const res = await fetch(`${BASE}/stores?${params}`);
  if (!res.ok) throw new Error('Erreur');
  return res.json();
}

/**
 * Supprime un template communautaire (propriétaire uniquement).
 * @param {number} templateId
 * @returns {Promise<void>}
 */
export async function deleteCommunityTemplate(templateId) {
  const headers = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}/templates/${templateId}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Erreur lors de la suppression');
  }
}
